/**
 * Every setting that exists, one import and one entry each.
 *
 * Array order is screen order, and it is the only thing an entry decides here: a section appears
 * where its first knob does, and knobs stack down that section in the order below. Everything about
 * what a knob does lives in its own file.
 *
 * Adding knob eleven adds `knobs/<quantity>.ts`, a name in `SettingId` and a line here. It edits
 * no part of the screen, no control and no save.
 */

import { ADS_LOOK_MULTIPLIER } from '@/ui/settings/knobs/adsLookMultiplier';
import { AIM_HOLD_MODE } from '@/ui/settings/knobs/aimHoldMode';
import { INVERT_LOOK_Y } from '@/ui/settings/knobs/invertLookY';
import { KEY_BINDINGS } from '@/ui/settings/knobs/keyBindings';
import { AUDIO_CAPTIONS } from '@/ui/settings/knobs/audioCaptions';
import { CAMERA_SHAKE } from '@/ui/settings/knobs/cameraShake';
import { CROUCH_HOLD_MODE } from '@/ui/settings/knobs/crouchHoldMode';
import { EFFECTS_LEVEL } from '@/ui/settings/knobs/effectsLevel';
import { FIRST_PERSON_FOV_DEGREES } from '@/ui/settings/knobs/firstPersonFovDegrees';
import { HEAD_BOB } from '@/ui/settings/knobs/headBob';
import { HUD_SCALE } from '@/ui/settings/knobs/hudScale';
import { HUD_SKIN } from '@/ui/settings/knobs/hudSkin';
import { LOOK_DEGREES_PER_COUNT } from '@/ui/settings/knobs/lookDegreesPerCount';
import { MASTER_LEVEL } from '@/ui/settings/knobs/masterLevel';
import { MOTION_REDUCTION } from '@/ui/settings/knobs/motionReduction';
import { MUSIC_LEVEL } from '@/ui/settings/knobs/musicLevel';
import { QUALITY_TIER_SETTING } from '@/ui/settings/knobs/qualityTier';
import { RARITY_COLOUR } from '@/ui/settings/knobs/rarityColour';
import { RENDER_SCALE } from '@/ui/settings/knobs/renderScale';
import { SPRINT_HOLD_MODE } from '@/ui/settings/knobs/sprintHoldMode';
import { THIRD_PERSON_FOV_DEGREES } from '@/ui/settings/knobs/thirdPersonFovDegrees';
import { UI_LEVEL } from '@/ui/settings/knobs/uiLevel';
import type { SettingDefinition } from '@/ui/settings/settingContract';

export const SETTINGS: readonly SettingDefinition[] = [
  LOOK_DEGREES_PER_COUNT,
  ADS_LOOK_MULTIPLIER,
  INVERT_LOOK_Y,
  AIM_HOLD_MODE,
  CROUCH_HOLD_MODE,
  SPRINT_HOLD_MODE,
  // Last in its section: the tall panel, under the knobs that are one row each.
  KEY_BINDINGS,
  MASTER_LEVEL,
  EFFECTS_LEVEL,
  MUSIC_LEVEL,
  UI_LEVEL,
  // The whole picture first, then the pixels it runs over, then the two lenses. The section reads
  // from the largest decision down.
  QUALITY_TIER_SETTING,
  RENDER_SCALE,
  FIRST_PERSON_FOV_DEGREES,
  THIRD_PERSON_FOV_DEGREES,
  HUD_SKIN,
  HUD_SCALE,
  // The master first, then the two it turns off, then the two channels that are not motion.
  // Registry order is reading order, and a switch that answers for the others has to be above them
  // or a player throws three and then finds the one that would have done it.
  MOTION_REDUCTION,
  HEAD_BOB,
  CAMERA_SHAKE,
  RARITY_COLOUR,
  AUDIO_CAPTIONS,
];
